/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  CompareGroupedResult,
  ConfigComponentArchivedSummary,
  ConfigQueryResults,
  Configuration,
  ConfigurationWithoutRequest,
  ESSCostingSimulationRequest,
  FIMOSimulationRequest,
  ListResultsQuery,
  PlantSimRequest,
  PVCostingRequest,
  PVSimulationRequest,
  RequestWithName,
} from '@poweralpha/common';
import type { PlantSimRequest as PlantSimRequestV2 } from '@poweralpha/common/models/plantsim';
import { useMutation, useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import type { HTTPError, KyResponse } from 'ky';
import { api, type QueryConfigsSearchParams } from '../../api';
import {
  editEssCostingConfiguration,
  getArchivedEssCostingComponents,
  getEssConfigMaxVersion,
  getEssCostingConfiguration,
  getEssCostingConfigurations,
  saveEssCostingConfiguration,
} from '../../ess/api';
import {
  editFIMOConfiguration,
  getFIMOConfigMaxVersion,
  getFIMOConfiguration,
  getFIMOConfigurations,
  saveFIMOConfiguration,
} from '../../fimo/api';
import {
  editPlantSimConfiguration,
  getPlantSimConfiguration,
  getPlantSimConfigurations,
  getPlantSimMaxVersion,
  savePlantSimConfiguration,
} from '../../plant/api';
import {
  editPlantSimConfiguration as editPlantSimV2Configuration,
  getPlantSimConfiguration as getPlantSimV2Configuration,
  getPlantSimConfigurations as getPlantSimV2Configurations,
  getPlantSimMaxVersion as getPlantSimV2MaxVersion,
  savePlantSimConfiguration as savePlantSimV2Configuration,
} from '../../plant-v2/api';
import {
  editPvCostingConfiguration,
  editPvsimConfiguration,
  getArchivedPvCostingComponents,
  getArchivedPvSimComponents,
  getPvCostingConfigMaxVersion,
  getPvCostingConfiguration,
  getPvCostingConfigurations,
  getPvsimConfigMaxVersion,
  getPvsimConfiguration,
  getPvsimConfigurations,
  savePvCostingConfiguration,
  savePvsimConfiguration,
} from '../../pv/api';
import { objectToQueryKey, queryClient } from '../../queryClient';
import type { MutationOptions, QueryOptions } from '../queries';
import type { ConfigType } from './models';

export type ConfigTypeToRequest = {
  pvsim: PVSimulationRequest;
  pv: PVCostingRequest;
  plantsim: PlantSimRequest;
  plantsimV2: PlantSimRequestV2;
  ess: ESSCostingSimulationRequest;
  fimo: FIMOSimulationRequest;
};

type EditMutationFnReturn<T extends ConfigType> = Promise<Configuration<ConfigTypeToRequest[T]>>;

export const useResultsGroupByConfigs = (
  path: string,
  opts?: QueryOptions<ListResultsQuery<CompareGroupedResult>> & {
    searchParams?: { configId?: string };
  },
) => {
  return useQuery({
    queryKey: ['results-group-by-configs', path, ...objectToQueryKey(opts?.searchParams)],
    queryFn: () => {
      return api
        .get(`${path}/results-group-by-configs`, {
          searchParams: opts?.searchParams,
        })
        .json<ListResultsQuery<CompareGroupedResult>>();
    },
    staleTime: opts?.searchParams?.configId ? Number.POSITIVE_INFINITY : 30 * 1e3,
    ...opts,
    enabled:
      Boolean(opts?.searchParams?.configId !== undefined ? opts.searchParams.configId : true) &&
      (opts?.enabled ?? true),
  });
};

export const useSaveAsMutation = <Type extends ConfigType>(
  configType: Type,
  opts?: MutationOptions<
    Configuration<ConfigTypeToRequest[Type]>,
    HTTPError,
    RequestWithName<ConfigTypeToRequest[Type]>
  >,
) =>
  useMutation({
    mutationFn: (request) => {
      switch (configType) {
        case 'pvsim':
          return savePvsimConfiguration(
            request as RequestWithName<PVSimulationRequest>,
          ) as EditMutationFnReturn<Type>;
        case 'pv':
          return savePvCostingConfiguration(
            request as RequestWithName<PVCostingRequest>,
          ) as EditMutationFnReturn<Type>;
        case 'plantsim':
          return savePlantSimConfiguration(
            request as RequestWithName<PlantSimRequest>,
          ) as EditMutationFnReturn<Type>;
        case 'plantsimV2':
          return savePlantSimV2Configuration(
            request as RequestWithName<PlantSimRequestV2>,
          ) as EditMutationFnReturn<Type>;
        case 'ess':
          return saveEssCostingConfiguration(
            request as RequestWithName<ESSCostingSimulationRequest>,
          ) as EditMutationFnReturn<Type>;
        case 'fimo':
          return saveFIMOConfiguration(
            request as RequestWithName<FIMOSimulationRequest>,
          ) as EditMutationFnReturn<Type>;
        default:
          throw new Error('Unsupported config type');
      }
    },
    ...opts,
  });

function getEditFn<T extends ConfigType>(
  type: T,
  id: string,
  version: number,
): (
  req: RequestWithName<ConfigTypeToRequest[T]>,
) => Promise<Configuration<ConfigTypeToRequest[T]>> {
  switch (type) {
    case 'pvsim':
      return (req) =>
        editPvsimConfiguration(
          req as RequestWithName<PVSimulationRequest>,
          id,
          version,
        ) as EditMutationFnReturn<T>;
    case 'pv':
      return (req) =>
        editPvCostingConfiguration(
          req as RequestWithName<PVCostingRequest>,
          id,
          version,
        ) as Promise<Configuration<ConfigTypeToRequest[T]>>;
    case 'plantsim':
      return (req) =>
        editPlantSimConfiguration(
          req as RequestWithName<PlantSimRequest>,
          id,
          version,
        ) as EditMutationFnReturn<T>;
    case 'plantsimV2':
      return (req) =>
        editPlantSimV2Configuration(
          req as RequestWithName<PlantSimRequestV2>,
          id,
          version,
        ) as EditMutationFnReturn<T>;
    case 'ess':
      return (req) =>
        editEssCostingConfiguration(
          req as RequestWithName<ESSCostingSimulationRequest>,
          id,
          version,
        ) as EditMutationFnReturn<T>;
    case 'fimo':
      return (req) =>
        editFIMOConfiguration(
          req as RequestWithName<FIMOSimulationRequest>,
          id,
          version,
        ) as EditMutationFnReturn<T>;
    default:
      throw new Error('Unsupported config type');
  }
}

export const useSaveMutation = <Type extends ConfigType>(
  configType: Type,
  id: string,
  version: number,
  opts?: MutationOptions<
    Configuration<ConfigTypeToRequest[Type]>,
    HTTPError,
    RequestWithName<ConfigTypeToRequest[Type]>
  >,
) => useMutation({ mutationFn: getEditFn(configType, id, version), ...opts });

export const useMaxVersionQuery = <T = number>(
  configType: ConfigType,
  id: string | undefined,
  opts?: QueryOptions<number, HTTPError, T>,
) =>
  useQuery({
    queryKey: [`${configType}-max-version`, id],
    queryFn: () => {
      switch (configType) {
        case 'pvsim':
          return getPvsimConfigMaxVersion(id);
        case 'pv':
          return getPvCostingConfigMaxVersion(id);
        case 'plantsim':
          return getPlantSimMaxVersion(id);
        case 'plantsimV2':
          return getPlantSimV2MaxVersion(id);
        case 'ess':
          return getEssConfigMaxVersion(id);
        case 'fimo':
          return getFIMOConfigMaxVersion(id);
        default:
          throw new Error('Unsupported config type');
      }
    },
    ...opts,
    enabled: Boolean(id) && (opts?.enabled ?? true),
  });

export const useConfigurationsQuery = <T = ConfigQueryResults<ConfigurationWithoutRequest>>(
  configType: ConfigType,
  opts?: QueryOptions<ConfigQueryResults<ConfigurationWithoutRequest>, HTTPError, T> & {
    extraQueryOptions?: QueryConfigsSearchParams;
  },
) => {
  const options = { latestVersionOnly: false, listOnly: true, ...opts?.extraQueryOptions };
  return useQuery({
    queryKey: [`${configType}-configs`, ...objectToQueryKey(options)],
    queryFn: () => {
      switch (configType) {
        case 'pvsim':
          return getPvsimConfigurations(options);
        case 'pv':
          return getPvCostingConfigurations(options);
        case 'plantsim':
          return getPlantSimConfigurations(options);
        case 'plantsimV2':
          return getPlantSimV2Configurations(options);
        case 'ess':
          return getEssCostingConfigurations(options);
        case 'fimo':
          return getFIMOConfigurations(options);
        default:
          throw new Error('Unsupported config type');
      }
    },
    ...opts,
  });
};

export const useConfigurationQuery = <Type extends ConfigType>(
  configType: Type,
  id: string | undefined,
  opts?: QueryOptions<Configuration<ConfigTypeToRequest[Type]>> & { version?: number },
) =>
  useQuery({
    queryKey: [`${configType}-config`, id, opts?.version],
    queryFn: () => {
      switch (configType) {
        case 'pvsim':
          return getPvsimConfiguration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        case 'pv':
          return getPvCostingConfiguration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        case 'plantsim':
          return getPlantSimConfiguration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        case 'plantsimV2':
          return getPlantSimV2Configuration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        case 'ess':
          return getEssCostingConfiguration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        case 'fimo':
          return getFIMOConfiguration(id, opts?.version) as Promise<
            Configuration<ConfigTypeToRequest[Type]>
          >;
        default:
          throw new Error('Unsupported config type');
      }
    },
    ...opts,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id) && (opts?.enabled ?? true),
  });

export const useArchiveConfigMutation = (configType: ConfigType) =>
  useMutation<KyResponse<unknown>, HTTPError, string | undefined>({
    mutationFn: (id) => {
      switch (configType) {
        case 'pv':
          return api.post(`pvsim/costing/configs/${id}/archive`);
        case 'plantsim':
        case 'plantsimV2':
          return api.post(`plant/configs/${id}/archive`);
        case 'ess':
        case 'pvsim':
        case 'fimo':
          return api.post(`${configType}/configs/${id}/archive`);
        default:
          throw new Error('Unsupported config type');
      }
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [`${configType}-config`, id] });
    },
  });

export const useConfigCheckArchivedComponentsQuery = (
  configType: ConfigType,
  id: string | undefined | null,
  opts?: QueryOptions<Array<ConfigComponentArchivedSummary>, HTTPError>,
) =>
  useQuery({
    queryKey: [`${configType}-config`, 'archived-components'],
    queryFn: () => {
      switch (configType) {
        case 'pvsim':
          return getArchivedPvSimComponents(id);
        case 'pv':
          return getArchivedPvCostingComponents(id);
        case 'ess':
          return getArchivedEssCostingComponents(id);
        default:
          throw new Error('Unsupported config type');
      }
    },
    ...opts,
    staleTime: minutesToMilliseconds(1),
    enabled: Boolean(id) && ['pvsim', 'pv', 'ess'].includes(configType) && (opts?.enabled ?? true),
  });
